import React, { useState, useEffect } from 'react';
import { storeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingBag, ShoppingCart, Plus, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const ProductForm = ({ product, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    image_url: product?.image_url || '',
    category: product?.category || '',
    stock: product?.stock || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {product ? 'Edit Product' : 'Add New Product'}
          </h3>
          <button onClick={onCancel} className="p-2 hover:bg-muted rounded-full">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Product Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              required
              data-testid="product-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              rows={3}
              required
              data-testid="product-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
                required
                data-testid="product-price-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Stock</label>
              <input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
                required
                data-testid="product-stock-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              placeholder="e.g., journals, meditation, wellness"
              required
              data-testid="product-category-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Image URL</label>
            <input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              placeholder="https://..."
              required
              data-testid="product-image-input"
            />
          </div>
          
          {formData.image_url && (
            <div className="rounded-xl overflow-hidden">
              <img src={formData.image_url} alt="Preview" className="w-full h-32 object-cover" />
            </div>
          )}
          
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 rounded-full bg-muted text-foreground font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 rounded-full bg-primary text-white font-semibold"
              data-testid="save-product-btn"
            >
              {product ? 'Update' : 'Create'} Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const Store = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await storeAPI.getProducts();
      setProducts(response.data.products);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (product) => {
    try {
      // Load Razorpay script if not loaded
      if (!document.getElementById('razorpay-script')) {
        const script = document.createElement('script');
        script.id = 'razorpay-script';
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Create order
      const response = await storeAPI.createOrder(product.id, 1);
      const { order_id, internal_order_id, amount, currency, key_id, product_name } = response.data;

      // Configure Razorpay options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'HearMeOut Store',
        description: product_name,
        order_id: order_id,
        handler: async function (razorpayResponse) {
          try {
            // Verify payment
            const verifyData = {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              order_id: internal_order_id
            };
            
            const result = await storeAPI.verifyPayment(verifyData);
            
            if (result.data.success) {
              toast.success('Order placed successfully!');
            }
          } catch (error) {
            toast.error('Payment verification failed');
            console.error('Verification error:', error);
          }
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
        },
        theme: {
          color: '#7C9A92'
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error('Payment failed: ' + response.error.description);
      });
      razorpay.open();

    } catch (error) {
      toast.error('Failed to create order');
      console.error('Order error:', error);
    }
  };

  const handleCreateProduct = async (data) => {
    try {
      await storeAPI.createProduct(data);
      toast.success('Product created successfully');
      setShowForm(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const handleUpdateProduct = async (data) => {
    try {
      await storeAPI.updateProduct(editingProduct.id, data);
      toast.success('Product updated successfully');
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await storeAPI.deleteProduct(productId);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="min-h-screen bg-background py-8" data-testid="store-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <ShoppingBag className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Wellness Store</h1>
          <p className="text-muted-foreground">Curated products for your mental health journey</p>
          
          {isAdmin && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 inline-flex items-center space-x-2 px-6 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
              data-testid="add-product-btn"
            >
              <Plus className="w-5 h-5" />
              <span>Add Product</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <div key={product.id} className="bg-card rounded-2xl overflow-hidden card-soft hover:scale-105 transition-transform border border-border relative" data-testid="product-card">
              {isAdmin && (
                <div className="absolute top-4 right-4 flex space-x-2 z-10">
                  <button
                    onClick={() => setEditingProduct(product)}
                    className="p-2 bg-background/90 rounded-full hover:bg-background"
                    data-testid="edit-product-btn"
                  >
                    <Pencil className="w-4 h-4 text-primary" />
                  </button>
                  <button
                    onClick={() => handleDeleteProduct(product.id)}
                    className="p-2 bg-background/90 rounded-full hover:bg-background"
                    data-testid="delete-product-btn"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              )}
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {product.name}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    ${product.price.toFixed(2)}
                  </span>
                  <button
                    onClick={() => handlePurchase(product)}
                    className="flex items-center space-x-2 px-6 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
                    data-testid="buy-button"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Buy Now</span>
                  </button>
                </div>
                {product.stock > 0 && product.stock < 10 && (
                  <p className="text-xs text-warning mt-2">Only {product.stock} left in stock!</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {!loading && products.length === 0 && (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 text-muted mx-auto mb-4" />
            <p className="text-muted-foreground">No products available at the moment</p>
          </div>
        )}
      </div>

      {showForm && (
        <ProductForm
          onSubmit={handleCreateProduct}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingProduct && (
        <ProductForm
          product={editingProduct}
          onSubmit={handleUpdateProduct}
          onCancel={() => setEditingProduct(null)}
        />
      )}
    </div>
  );
};
